import { ComparisonDelta } from "@/analytics/components/ComparisonDelta";
import { ExploreAreaChart } from "@/analytics/components/ExploreAreaChart";
import { ExploreChartSkeleton } from "@/analytics/components/ExploreChartSkeleton";
import { ExploreContributionPie } from "@/analytics/components/ExploreContributionPie";
import { MetricNav } from "@/analytics/components/MetricNav";
import { CompareSelect } from "@/analytics/components/CompareSelect";
import { PeriodSelect } from "@/analytics/components/PeriodSelect";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useExploreData } from "@/analytics/dashboards/explore/hooks/useExploreData";
import { useExploreParams } from "@/analytics/dashboards/explore/hooks/useExploreParams";
import { AppLayout } from "@/components/layout/AppLayout";
import { exploreMetricsFor } from "@/grid/config/catalog";
import { formatMetricValue } from "@/grid/core/format-metric";
import { getDepartment, isDepartmentId } from "@/grid/config/registry";
import { buildStableColorIndexMap, entityColorKey } from "@/analytics/core/chart-palette";
import { cn } from "@/lib/cn";
import { Navigate, useParams } from "react-router-dom";
import { useMemo } from "react";

export function ExplorePage() {
  const { departmentId = "sales" } = useParams<{ departmentId: string }>();
  const department = getDepartment(departmentId);

  if (!isDepartmentId(departmentId) || !department) {
    return <Navigate to="/explore/sales" replace />;
  }

  const sidebarMetrics = exploreMetricsFor(department);
  const defaultMetricId = sidebarMetrics[0]!.id;
  const { params, period, compare, updateParams, setTeams, setReps } = useExploreParams(
    defaultMetricId,
    department,
  );
  const { data, isLoading, isFetching, error } = useExploreData(
    departmentId,
    params,
    period,
    compare,
  );

  const view = data?.view;

  const teamColorIndexMap = useMemo(
    () =>
      buildStableColorIndexMap(
        (data?.teamOptions ?? [])
          .map((team) => team.colorKey)
          .filter((key): key is string => key != null),
      ),
    [data?.teamOptions],
  );

  const repColorIndexMap = useMemo(
    () =>
      buildStableColorIndexMap(
        (data?.repOptions ?? []).map((rep) => entityColorKey("rep", rep.id)),
      ),
    [data?.repOptions],
  );

  return (
    <AppLayout contentClassName="px-4 pb-8 sm:px-6">
      <div className="mx-auto flex min-h-[calc(100dvh-4rem)] max-w-[1400px] gap-6 py-4">
        <aside className="hidden w-52 shrink-0 md:block">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Metrics
          </p>
          <MetricNav
            metrics={sidebarMetrics}
            activeId={params.metricId}
            onChange={(metricId) => updateParams({ metricId })}
          />
        </aside>

        <main className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center gap-3 md:hidden">
            <Select
              value={params.metricId}
              onValueChange={(metricId) => updateParams({ metricId })}
            >
              <SelectTrigger className="min-w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sidebarMetrics.map((metric) => (
                  <SelectItem key={metric.id} value={metric.id}>
                    {metric.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-6 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <MultiCombobox
                    allLabel="All teams"
                    colorKeyPrefix="team"
                    entityColorIndexMap={teamColorIndexMap}
                    options={data?.teamOptions ?? []}
                    selectedIds={params.selection.teamIds}
                    onApply={setTeams}
                    searchPlaceholder="Search teams…"
                  />
                  {data?.showRepPicker ? (
                    <MultiCombobox
                      allLabel="All reps"
                      colorKeyPrefix="rep"
                      entityColorIndexMap={repColorIndexMap}
                      options={data.repOptions}
                      selectedIds={params.selection.repIds}
                      onApply={setReps}
                      searchPlaceholder="Search reps…"
                    />
                  ) : null}
                </div>

                <PeriodSelect
                  periodSpec={params.periodSpec}
                  onChange={(periodSpec) => updateParams({ periodSpec })}
                  trailing={
                    <CompareSelect
                      value={params.compareMode}
                      onChange={(compareMode) => updateParams({ compareMode })}
                    />
                  }
                />
              </div>
              <p className="text-sm text-[var(--color-text-muted)]">{period.label}</p>
            </div>

            {isLoading && !data ? <ExploreChartSkeleton /> : null}

            {error ? (
              <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error instanceof Error ? error.message : "Failed to load explore data"}
              </p>
            ) : null}

            {view ? (
              <div
                className={cn(
                  "transition-opacity duration-200",
                  isFetching ? "opacity-60" : "opacity-100",
                )}
              >
                <div className="mb-2 flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-[#94A3B8]">{view.metric.label}</p>
                    <p className="text-4xl font-semibold tracking-tight text-[#334155] sm:text-5xl">
                      {formatMetricValue(view.total, view.metric.format)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <ComparisonDelta deltaPct={view.deltaPct} compareLabel={compare.label} />
                    {view.contributions.length > 1 ? (
                      <ExploreContributionPie
                        slices={view.contributions}
                        format={view.metric.format}
                      />
                    ) : null}
                  </div>
                </div>

                <ExploreAreaChart
                  series={view.series}
                  compareSeries={view.series.length === 1 ? view.compareSeries : []}
                  format={view.metric.format}
                  compareLabel={compare.label}
                  className="mt-4"
                />
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </AppLayout>
  );
}
